import { convertBorderToCSS } from '../convert-border-to-css';
import { convertMarginToCSS } from '../convert-margin-to-css';
import { convertPaddingToCSS } from '../convert-padding-to-css';
import { ensureUnit } from '../ensure-unit';
import type { StyleAttributes } from './types';

/**
 * Generate inline styles object from attributes
 *
 * @param {StyleAttributes} attributes - Block attributes containing style data
 * @returns {React.CSSProperties} Inline styles object
 */
export function generateInlineStyles(
	attributes: StyleAttributes,
): React.CSSProperties {
	const styles: React.CSSProperties = {};

	// Typography styles
	if ( attributes.style?.typography ) {
		const typography = attributes.style.typography;
		if ( typography.fontSize ) {
			styles.fontSize = ensureUnit( typography.fontSize );
		}
		if ( typography.fontWeight ) {
			styles.fontWeight = typography.fontWeight;
		}
		if ( typography.textTransform ) {
			styles.textTransform =
				typography.textTransform as React.CSSProperties[ 'textTransform' ];
		}
		if ( typography.lineHeight ) {
			styles.lineHeight = typography.lineHeight;
		}
		if ( typography.letterSpacing ) {
			styles.letterSpacing = ensureUnit( typography.letterSpacing );
		}
	}

	// Color styles
	if ( attributes.style?.color ) {
		const color = attributes.style.color;
		if ( color.text ) {
			styles.color = color.text;
		}
		if ( color.background ) {
			styles.backgroundColor = color.background;
		}
		if ( color.border ) {
			styles.borderColor = color.border;
		}
	}

	// Spacing styles
	if ( attributes.style?.spacing ) {
		const spacing = attributes.style.spacing;
		if ( spacing.margin ) {
			const marginCSS = convertMarginToCSS( spacing.margin );
			Object.assign( styles, marginCSS );
		}
		if ( spacing.padding ) {
			const paddingCSS = convertPaddingToCSS( spacing.padding );
			Object.assign( styles, paddingCSS );
		}
	}

	// Border styles
	if ( attributes.style?.border ) {
		const borderCSS = convertBorderToCSS( attributes.style.border );
		Object.assign( styles, borderCSS );
	}

	// Layout styles
	if ( attributes.style?.layout ) {
		const layout = attributes.style.layout;
		if ( layout.display ) {
			styles.display = layout.display as React.CSSProperties[ 'display' ];
		}
		if ( layout.flexDirection ) {
			styles.flexDirection =
				layout.flexDirection as React.CSSProperties[ 'flexDirection' ];
		}
		if ( layout.justifyContent ) {
			styles.justifyContent =
				layout.justifyContent as React.CSSProperties[ 'justifyContent' ];
		}
		if ( layout.alignItems ) {
			styles.alignItems =
				layout.alignItems as React.CSSProperties[ 'alignItems' ];
		}
		if ( layout.gap ) {
			styles.gap = ensureUnit( layout.gap );
		}
	}

	// Legacy support
	if ( attributes.backgroundColor ) {
		styles.backgroundColor = attributes.backgroundColor;
	}
	if ( attributes.textColor ) {
		styles.color = attributes.textColor;
	}
	if ( attributes.fontSize ) {
		styles.fontSize = ensureUnit( attributes.fontSize );
	}
	if ( attributes.fontWeight ) {
		styles.fontWeight = attributes.fontWeight;
	}
	if ( attributes.borderColor ) {
		styles.borderColor = attributes.borderColor;
	}

	// Custom CSS
	if ( attributes.customCSS ) {
		Object.entries( attributes.customCSS ).forEach( ( [ prop, value ] ) => {
			if ( value ) {
				// Convert kebab-case to camelCase for React
				const camelProp = prop.replace(
					/-([a-z])/g,
					( match, letter ) => letter.toUpperCase(),
				);
				( styles as Record< string, string | number > )[ camelProp ] =
					value;
			}
		} );
	}

	return styles;
}
